import React from "react";
import { motion } from "framer-motion";
import { HelmetProvider } from "react-helmet-async";
import SEOHead from "../components/common/SEOHead";
import { getSEOData } from "../utils/seoConfig";

const Rules = () => {
  const seoData = getSEOData("rules");

  const rules = [
    {
      category: "Eligibility",
      items: [
        "Open to all developers regardless of experience level",
        "Teams can have 1-5 members maximum",
        "Participants must be 13 years or older",
      ],
    },
    {
      category: "Development",
      items: [
        "Games must be created during the competition period",
        "Pre-existing code libraries and assets are allowed",
        "Use any game engine or programming language",
        "All assets must be properly licensed",
        "Source code must be available for review",
        "AI assisted development is allowed",
      ],
    },
    {
      category: "Submission",
      items: [
        "Games must be playable on Windows or Web",
        "Include a playable build and source code",
        "Provide a game description, screenshots and trailer",
        "Submit before the deadline (no extensions)",
        "One submission per team/individual",
      ],
    },
    {
      category: "Content Guidelines",
      items: [
        "No explicit violence or gore",
        "No discriminatory or offensive content",
        "No use of copyrighted characters or brands",
        "No use of copyrighted music or sound effects without permission",
        "Games should be appropriate for all ages",
        "Games must be original and not previously published",
        "Respect cultural sensitivities",
      ],
    },
    {
      category: "Judging Criteria",
      items: [
        "Creativity",
        "Fun Factor",
        "Playability",
        "Game Mechanic Implementation",
        "Audio & Visuals",
      ],
    },
    {
      category: "Prizes and Recognition",
      items: [
        "Winners announced at closing ceremony",
        "Prizes cannot be transferred or exchanged",
        "Tax obligations are winner's responsibility",
        "Recognition includes portfolio opportunities",
        "Industry connections for delegates",
      ],
    },
  ];

  return (
    <HelmetProvider>
      <div className="exe-bg-pattern min-h-screen pt-20">
        <SEOHead
          title={seoData.title}
          description={seoData.description}
          image={seoData.image}
          url={seoData.url}
          keywords={seoData.keywords}
        />

        <div className="exe-container py-16">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-4xl mx-auto"
          >
            <div className="text-center mb-12">
              <h1 className="exe-title text-exe-text-accent mb-6">
                Competition Rules
              </h1>
              <p className="exe-text text-xl">
                Guidelines and regulations for EXE competition participants
              </p>
            </div>

            <div className="space-y-8">
              {rules.map((section, index) => (
                <motion.div
                  key={section.category}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="exe-glass-card p-8"
                >
                  <h2 className="exe-heading text-exe-text-accent mb-6">
                    {section.category}
                  </h2>
                  <ul className="space-y-3">
                    {section.items.map((item, itemIndex) => (
                      <motion.li
                        key={itemIndex}
                        initial={{ opacity: 0, x: -20 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.2, delay: itemIndex * 0.025 }}
                        viewport={{ once: true }}
                        className="exe-text flex items-start gap-3"
                      >
                        <span className="text-exe-text-accent text-xl leading-none">
                          •
                        </span>
                        <span>{item}</span>
                      </motion.li>
                    ))}
                  </ul>
                </motion.div>
              ))}

              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3 }}
                viewport={{ once: true }}
                className="exe-glass-card p-8"
              >
                <h2 className="exe-heading text-exe-text-accent mb-6">
                  Important Notes
                </h2>

                <div className="bg-exe-bg-tertiary p-6 rounded-lg mb-6">
                  <h3 className="font-semibold text-exe-text-primary mb-3">
                    Code of Conduct
                  </h3>
                  <p className="exe-text">
                    All participants are expected to maintain professional and
                    respectful behavior throughout the competition. Harassment,
                    discrimination, or unsportsmanlike conduct will result in
                    immediate disqualification.
                  </p>
                </div>

                <div className="bg-exe-bg-tertiary p-6 rounded-lg mb-6">
                  <h3 className="font-semibold text-exe-text-primary mb-3">
                    Intellectual Property
                  </h3>
                  <p className="exe-text">
                    Participants retain full ownership of their creations. By
                    submitting, you grant EXE organizers permission to showcase
                    your game for promotional purposes and competition
                    documentation.
                  </p>
                </div>

                <div className="bg-exe-bg-tertiary p-6 rounded-lg">
                  <h3 className="font-semibold text-exe-text-primary mb-3">
                    Support and Questions
                  </h3>
                  <p className="exe-text mb-4">
                    Need clarification on any rules? Have technical questions?
                    Our team is here to help:
                  </p>
                  <div className="space-y-2 exe-text">
                    <p>• Join our Discord community for real-time support</p>
                    <p>• Email us at levelstair@gmail.com</p>
                  </div>
                </div>
              </motion.div>
            </div>

            <div className="text-center mt-12">
              <p className="exe-text mb-6">
                By participating in EXE, you agree to abide by all competition
                rules and guidelines.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.a
                  href="/register"
                  className="exe-btn exe-btn-primary"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Register for Competition
                </motion.a>

                <motion.a
                  href="/contact"
                  className="exe-btn exe-btn-outline"
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Contact Support
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </HelmetProvider>
  );
};

export default Rules;
